// packages/base/src/mention-autocomplete/mention-autocomplete.ts
//
// Story W4-S6 / W4-S6b — pure-Node mention-candidate ranker lifted from the
// SPA's local copy (`packages/web-ui/src/components/mention-helper/rank-mentions.ts`)
// into `@tessa-lean/base` so the SPA `<MentionPopup>`, the server route
// `GET /api/v1/kb/mentions`, the legacy webview-ui Lit `<mention-suggestions>`,
// and any future automation share one ranking implementation.
//
// References:
//   - epics-tessa-lean-web-ui.md §"#### W4-S6" lines 1241-1271 (story spec)
//   - architecture-tessa-lean-web-ui.md §OI-4 (story-locked weights blend)
//   - prd-tessa-lean-web-ui.md FR-KB-4, FR-BACK-7, NFR-PERF-9
//
// Algorithm (mirrors AC #1 §"Algorithm" verbatim):
//   score = w.fuzzy * fuzzyScore(prefix, qualifiedName)
//         + w.recency * recencyScore(entityId, now())
//         + w.kindWeight * (kindWeights[kind] ?? 1.0)
// Sort: (score DESC, simpleName.length ASC, qualifiedName ASC)
// Drop: candidates with fuzzyScore === 0
// Map: KbEntity → MentionCandidate (chipText = simple name).
//
// Pure: no I/O, no mutation, deterministic when `opts.now` is injected.
// Throws nothing — defensive against malformed input (returns []).
/**
 * Story-locked default weights — see W4-S6 AC #4 + Arch OI-4. Frozen so
 * tests cannot mutate the shared default and poison parallel runs.
 */
export const DEFAULT_RANK_WEIGHTS = Object.freeze({
    fuzzy: 0.7,
    recency: 0.2,
    kindWeight: 0.1,
});
export const DEFAULT_LIMIT = 20;
export const MAX_LIMIT = 500;
const MS_PER_DAY = 86_400_000;
// Story F1: added `'interface-def'` + `'connection-def'`. Same default
// weight as the legacy kinds; the rank UI scopes by kind, so the
// structured kinds rank identically to their pre-F1 stub siblings.
const DEFAULT_KIND_WEIGHTS = Object.freeze({
    part: 1.0,
    'part-usage': 1.0,
    port: 1.0,
    interface: 1.0,
    'interface-def': 1.0,
    connection: 1.0,
    'connection-def': 1.0,
    'value-attribute': 1.0,
});
/** Hoisted no-op recency lookup — avoids per-call closure allocation. */
const NO_RECENCY = () => undefined;
/** Extract the simple name (segment after the last `::`) — falls back to qn. */
function simpleName(qualifiedName) {
    const idx = qualifiedName.lastIndexOf('::');
    if (idx === -1)
        return qualifiedName;
    return qualifiedName.slice(idx + 2);
}
/**
 * Fuzzy score ladder (W4-S6 AC #1 §"Algorithm"):
 *   1.0  — exact case-insensitive prefix match on the simple name
 *   0.85 — exact prefix match on `qualifiedName`
 *   0.7  — case-insensitive substring match anywhere in `qualifiedName`
 *   0.0  — no match
 */
function fuzzyScore(prefixLower, qualifiedName) {
    if (prefixLower.length === 0)
        return 0;
    const qnLower = qualifiedName.toLowerCase();
    const simpleLower = simpleName(qualifiedName).toLowerCase();
    if (simpleLower.startsWith(prefixLower))
        return 1.0;
    if (qnLower.startsWith(prefixLower))
        return 0.85;
    if (qnLower.includes(prefixLower))
        return 0.7;
    return 0;
}
/** Coerce `recencyByEntityId` (Map | Record | undefined) to a getter. */
function makeRecencyLookup(raw) {
    if (raw === undefined)
        return NO_RECENCY;
    if (raw instanceof Map) {
        return (id) => raw.get(id);
    }
    // Record<string, number>
    const record = raw;
    return (id) => {
        const v = record[id];
        return typeof v === 'number' ? v : undefined;
    };
}
function recencyScore(lastUsedMs, nowMs) {
    if (lastUsedMs === undefined)
        return 0;
    const ageDays = Math.max(0, (nowMs - lastUsedMs) / MS_PER_DAY);
    return 1 / (1 + ageDays);
}
/**
 * Rank `candidates` by relevance to `prefix` and return the top N as
 * `MentionCandidate[]`. See file header for the algorithm contract.
 *
 * Defensive: returns `[]` when `prefix` is empty (matches the
 * `MentionsQuerySchema.prefix: minLength 1` server constraint). Never
 * throws — malformed input collapses to an empty array.
 */
export function rankCandidates(prefix, candidates, opts = {}) {
    if (prefix.length === 0)
        return [];
    const weights = {
        fuzzy: opts.weights?.fuzzy ?? DEFAULT_RANK_WEIGHTS.fuzzy,
        recency: opts.weights?.recency ?? DEFAULT_RANK_WEIGHTS.recency,
        kindWeight: opts.weights?.kindWeight ?? DEFAULT_RANK_WEIGHTS.kindWeight,
    };
    const kindWeights = opts.kindWeights ?? DEFAULT_KIND_WEIGHTS;
    const recencyLookup = makeRecencyLookup(opts.recencyByEntityId);
    // Only call the clock when recency contributes; saves a syscall on the hot path.
    const needsNow = weights.recency !== 0 && opts.recencyByEntityId !== undefined;
    const nowMs = needsNow ? (opts.now ?? Date.now)() : 0;
    const prefixLower = prefix.toLowerCase();
    const limit = Math.min(Math.max(1, opts.limit ?? DEFAULT_LIMIT), MAX_LIMIT);
    // Score + filter (drop zero-fuzzy).
    const scored = [];
    for (const entity of candidates) {
        const fuzzy = fuzzyScore(prefixLower, entity.qualifiedName);
        if (fuzzy === 0)
            continue;
        const recency = needsNow
            ? recencyScore(recencyLookup(entity.entityId), nowMs)
            : 0;
        const kindMul = kindWeights[entity.kind] ?? 1.0;
        const score = weights.fuzzy * fuzzy +
            weights.recency * recency +
            weights.kindWeight * kindMul;
        const simple = simpleName(entity.qualifiedName);
        scored.push({
            entity,
            score,
            simpleName: simple,
            simpleNameLength: simple.length,
        });
    }
    // Sort: score DESC, simpleName.length ASC, qualifiedName ASC.
    scored.sort((a, b) => {
        if (a.score !== b.score)
            return b.score - a.score;
        if (a.simpleNameLength !== b.simpleNameLength)
            return a.simpleNameLength - b.simpleNameLength;
        return a.entity.qualifiedName.localeCompare(b.entity.qualifiedName);
    });
    const top = scored.slice(0, limit);
    return top.map(({ entity, simpleName: chip }) => ({
        chipText: chip,
        entityId: entity.entityId,
        kind: entity.kind,
        qualifiedName: entity.qualifiedName,
    }));
}
//# sourceMappingURL=mention-autocomplete.js.map